'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { Brand } from '@/components/Brand';
import { LangProvider } from '@/lib/i18n';
import type { Lang } from '@/lib/i18n';
import { isLang } from '@/lib/i18n/types';

export default function MentionsLegalesPage() {
  const [lang, setLang] = useState<Lang>('fr');
  useEffect(() => {
    const v = new URLSearchParams(window.location.search).get('lang');
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (isLang(v)) setLang(v);
  }, []);

  return (
    <LangProvider value={lang}>
      <Body lang={lang} />
    </LangProvider>
  );
}

function Body({ lang }: { lang: Lang }) {
  const isFr = lang === 'fr';
  const homeHref = isFr ? '/' : '/?lang=en';
  return (
    <div className="flex flex-col min-h-full">
      <header className="sticky top-0 z-30 border-b border-border bg-background/85 backdrop-blur pt-[env(safe-area-inset-top)]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
          <Link href={homeHref} aria-label={isFr ? "Retour à l'accueil" : 'Back to home'}>
            <Brand size="md" />
          </Link>
          <Link
            href={homeHref}
            className="text-sm font-medium text-muted-foreground hover:text-foreground"
          >
            {isFr ? '← Retour' : '← Back'}
          </Link>
        </div>
      </header>

      <main className="flex-1 max-w-3xl mx-auto w-full px-4 sm:px-6 py-8 sm:py-12 space-y-6">
        <h1
          className="font-heading font-extrabold text-3xl sm:text-4xl tracking-tight"
          style={{ color: 'var(--color-ocean)' }}
        >
          {isFr ? 'Mentions légales' : 'Legal notice'}
        </h1>

        <section className="space-y-2">
          <h2 className="font-heading font-bold text-lg text-foreground">
            {isFr ? 'Éditeur du site' : 'Site publisher'}
          </h2>
          <p className="text-sm leading-relaxed text-foreground/85">
            {isFr ? 'FanGass est édité par ' : 'FanGass is published by '}
            <strong>Morgan Haillus</strong>
            {isFr ? ', fondateur de l’agence ' : ', founder of '}
            <a
              href="https://katchak-agency.fr"
              target="_blank"
              rel="noopener noreferrer"
              className="underline underline-offset-2 font-semibold"
              style={{ color: 'var(--color-ocean)' }}
            >
              Katchak
            </a>
            .
          </p>
          <p className="text-sm leading-relaxed text-foreground/85">
            {isFr ? 'Contact : ' : 'Contact: '}
            <a
              href={`https://wa.me/590690346366?text=${encodeURIComponent(
                isFr ? 'Bonjour, je viens de fangass.com.' : 'Hi, I came from fangass.com.',
              )}`}
              target="_blank"
              rel="noopener noreferrer"
              className="underline underline-offset-2"
            >
              WhatsApp +590 690 34 63 66
            </a>
            .
          </p>
          <p className="text-sm leading-relaxed text-foreground/85">
            {isFr ? 'Profil : ' : 'Profile: '}
            <a
              href="https://www.linkedin.com/in/morganhaillus/"
              target="_blank"
              rel="noopener noreferrer"
              className="underline underline-offset-2"
            >
              {isFr ? 'LinkedIn de Morgan Haillus' : "Morgan Haillus' LinkedIn"}
            </a>
            .
          </p>
        </section>

        <section className="space-y-2">
          <h2 className="font-heading font-bold text-lg text-foreground">
            {isFr ? 'Hébergement' : 'Hosting'}
          </h2>
          <p className="text-sm leading-relaxed text-foreground/85">
            {isFr ? 'Le site est hébergé par ' : 'The site is hosted by '}
            <strong>Vercel Inc.</strong>, 340 S Lemon Ave #4133, Walnut, CA 91789, USA.
          </p>
        </section>

        <section className="space-y-2">
          <h2 className="font-heading font-bold text-lg text-foreground">
            {isFr ? 'Propriété intellectuelle' : 'Intellectual property'}
          </h2>
          <p className="text-sm leading-relaxed text-foreground/85">
            {isFr
              ? 'Le contenu éditorial (bulletins, illustrations, textes) est la propriété de Morgan Haillus / Katchak. Les données sources affichées proviennent de services publics (Météo-France Guadeloupe / Martinique / Iles du Nord, DEAL Guadeloupe, NOAA AOML, USF Optical Oceanography Lab) et d’observatoires régionaux (CARICOOS, Sargassum Information Hub / AIR Centre) ; chacun reste propriétaire de ses données. La carte est fournie par '
              : 'The editorial content (forecasts, illustrations, copy) is the property of Morgan Haillus / Katchak. Source data shown comes from public services (Météo-France Guadeloupe / Martinique / Iles du Nord, DEAL Guadeloupe, NOAA AOML, USF Optical Oceanography Lab) and regional observatories (CARICOOS, Sargassum Information Hub / AIR Centre); each remains the owner of its data. Map tiles by '}
            <a
              href="https://www.openstreetmap.org/copyright"
              target="_blank"
              rel="noopener noreferrer"
              className="underline underline-offset-2"
            >
              OpenStreetMap
            </a>
            .
          </p>
        </section>

        <section className="space-y-2">
          <h2 className="font-heading font-bold text-lg text-foreground">
            {isFr ? 'Limites de responsabilité' : 'Limits of liability'}
          </h2>
          <p className="text-sm leading-relaxed text-foreground/85">
            {isFr
              ? 'FanGass est un outil de vulgarisation. Il synthétise des bulletins publiés par des services météorologiques et scientifiques officiels et les reformule dans un ton accessible. Il ne remplace ni les bulletins officiels de Météo-France ni les consignes des autorités sanitaires (ARS, préfectures). En cas de doute sur la qualité de l’air ou la baignabilité d’une plage, fie-toi aux sources officielles.'
              : 'FanGass is an outreach tool. It synthesises bulletins published by official meteorological and scientific services and reformulates them in plain language. It does not replace official Météo-France forecasts or health authority instructions (ARS, prefectures). When in doubt about air quality or whether a beach is safe to swim, trust the official sources.'}
          </p>
        </section>

        <section className="space-y-2">
          <h2 className="font-heading font-bold text-lg text-foreground">
            {isFr ? 'Signaler un problème' : 'Report an issue'}
          </h2>
          <p className="text-sm leading-relaxed text-foreground/85">
            {isFr
              ? 'Si tu vois une plage mal scorée ou un bug, écris-moi via le bouton WhatsApp en bas de chaque page.'
              : 'If you see a miscored beach or a bug, message me via the WhatsApp button at the bottom of every page.'}
          </p>
        </section>

        <p className="text-xs text-muted-foreground pt-4">
          {isFr ? 'Dernière mise à jour : mai 2026.' : 'Last updated: May 2026.'}
        </p>
      </main>

      <footer className="border-t border-border bg-card mt-6">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4 text-xs text-muted-foreground flex flex-wrap gap-x-3 gap-y-1">
          <Link href={homeHref} className="hover:text-foreground">
            {isFr ? 'Accueil' : 'Home'}
          </Link>
          <span>·</span>
          <Link
            href={isFr ? '/mentions-legales' : '/mentions-legales?lang=en'}
            className="hover:text-foreground"
          >
            {isFr ? 'Mentions légales' : 'Legal notice'}
          </Link>
          <span>·</span>
          <Link
            href={isFr ? '/confidentialite' : '/confidentialite?lang=en'}
            className="hover:text-foreground"
          >
            {isFr ? 'Confidentialité' : 'Privacy'}
          </Link>
        </div>
      </footer>
    </div>
  );
}
